import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card } from '../ui/card';
import { Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { getTeacherById } from '@/services/db/teachers';
import { toast } from 'sonner';
import { getHeaderHtml, getFooterHtml, getCommonPrintStyles } from '@/utils/printing/printStyles';
import { downloadAsJPG, createPrintDocument } from '@/utils/printing/printingBridge';

interface Payment {
  id: string;
  date: string;
  amount: number;
  paymentPeriod?: string;
  status: string;
  method?: string;
  allowances?: any[];
  deductions?: any[];
  totalAllowances?: number;
  totalDeductions?: number;
  baseAmount?: number;
}

export const TeacherPaymentHistory = ({ teacherId }: { teacherId: string }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  
  useEffect(() => {
    const paymentsKey = `teacher_payments_${teacherId}`;
    const storedPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
    setPayments(storedPayments);
  }, [teacherId]);
  
  const handlePrintPaymentHistory = async () => {
    if (payments.length === 0) {
      toast.error('No payment history to download');
      return;
    }
    
    try {
      // Show loading toast
      const loadingToast = toast.loading('Generating payment history JPG...');
      
      // Get teacher data for header info
      const teacherData = await getTeacherById(teacherId);
      const teacherName = teacherData ? `${teacherData.firstName} ${teacherData.lastName}` : 'Unknown Teacher';
      
      // Use idNumber for display (the simple numeric ID like 1, 2, 3) instead of the UUID id
      const displayId = teacherData && teacherData.idNumber && teacherData.idNumber.trim() !== '' ? teacherData.idNumber : 'N/A';
      
      // Set up title and styles
      const title = 'Teacher Payment History';
      const styles = `
        ${getCommonPrintStyles()}
        .payment-history-content {
          margin-top: 15px;
        }
        .teacher-info {
          margin-bottom: 20px;
          padding: 15px;
          border: 1px solid #ddd;
          background-color: #f9f9f9;
          border-radius: 5px;
        }
        .teacher-name {
          font-size: 1.2em;
          font-weight: bold;
          color: #3D7069;
          margin-bottom: 5px;
        }
        .summary {
          margin-top: 15px;
          font-weight: bold;
          font-size: 1em;
          padding: 10px;
          background-color: #f0f7f5;
          border-radius: 5px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          font-size: 0.9em;
        }
        th {
          background-color: #f0f7f5;
          border: 1px solid #deecea;
          padding: 8px;
          text-align: left;
          font-weight: bold;
          color: #3D7069;
        }
        td {
          border: 1px solid #deecea;
          padding: 8px;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .amount-column {
          text-align: right;
        }
        .page-content {
          min-height: 800px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .footer {
          margin-top: auto;
          page-break-inside: avoid;
        }
      `;
      
      // Calculate total
      const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
      
      // Create table rows
      const tableRows = payments.map(payment => `
        <tr>
          <td>${payment.paymentPeriod || format(new Date(payment.date), 'MMMM yyyy')}</td>
          <td>${format(new Date(payment.date), 'PP')}</td>
          <td>${payment.method || 'N/A'}</td>
          <td class="amount-column">${payment.amount} AFN</td>
          <td>${payment.status}</td>
        </tr>
      `).join('');
      
      // Create HTML content
      const content = `
        <div class="page-content">
          ${getHeaderHtml(title)}
          
          <div class="payment-history-content">
            <div class="teacher-info">
              <div class="teacher-name">${teacherName}</div>
              <div>Teacher ID: ${displayId}</div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Payment Period</th>
                  <th>Date</th>
                  <th>Method</th>
                  <th class="amount-column">Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
            
            <div class="summary">
              <div>Total Payments: ${payments.length}</div>
              <div>Total Amount: ${totalPaid.toFixed(2)} AFN</div>
            </div>
          </div>
          
          ${getFooterHtml('teacher')}
        </div>
      `;
      
      // Generate the complete document
      const printDocument = createPrintDocument(content, `Payment_History_${teacherName}`, styles);
      
      // Download as JPG directly - no window opening
      await downloadAsJPG(printDocument, `Payment_History_${teacherName.replace(/\s+/g, '_')}`, {
        onError: (error) => {
          console.error('JPG download error:', error);
          toast.error('Failed to download payment history');
        }
      });
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('Payment history downloaded as JPG');
      
    } catch (error) {
      console.error('Error downloading payment history:', error);
      toast.error('Failed to download payment history');
    }
  };
  
  const handlePrintReceipt = async (payment: Payment) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Generating payment receipt JPG...');
      
      // Get full teacher data
      const teacherData = await getTeacherById(teacherId);
      if (!teacherData) {
        toast.error("Could not retrieve teacher data for receipt");
        return;
      }
      
      const teacherName = `${teacherData.firstName} ${teacherData.lastName}`;
      
      // Use idNumber for display (the simple numeric ID like 1, 2, 3) instead of the UUID id
      const displayId = teacherData.idNumber && teacherData.idNumber.trim() !== '' ? teacherData.idNumber : 'N/A';
      
      // Set up title and styles for individual receipt
      const title = 'Payment Receipt';
      const styles = `
        ${getCommonPrintStyles()}
        .receipt-content {
          margin-top: 15px;
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 20px;
          padding: 15px;
          border: 2px solid #3D7069;
          background-color: #f0f7f5;
          border-radius: 8px;
        }
        .receipt-title {
          font-size: 1.4em;
          font-weight: bold;
          color: #3D7069;
          margin-bottom: 5px;
        }
        .teacher-info, .payment-details {
          margin-bottom: 20px;
          padding: 15px;
          border: 1px solid #ddd;
          background-color: #f9f9f9;
          border-radius: 5px;
        }
        .section-title {
          font-size: 1.1em;
          font-weight: bold;
          color: #3D7069;
          margin-bottom: 10px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 5px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 5px 0;
        }
        .info-label {
          font-weight: bold;
          color: #555;
        }
        .info-value {
          color: #333;
        }
        .amount-highlight {
          font-size: 1.2em;
          font-weight: bold;
          color: #3D7069;
          text-align: center;
          padding: 10px;
          background-color: #e8f5e8;
          border-radius: 5px;
          margin: 15px 0;
        }
        .page-content {
          min-height: 800px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .footer {
          margin-top: auto;
          page-break-inside: avoid;
        }
      `;
      
      // Create receipt content
      const content = `
        <div class="page-content">
          ${getHeaderHtml(title)}
          
          <div class="receipt-content">
            <div class="receipt-header">
              <div class="receipt-title">SALARY PAYMENT RECEIPT</div>
              <div>Receipt #: ${payment.id}</div>
            </div>
            
            <div class="teacher-info">
              <div class="section-title">Teacher Information</div>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${teacherName}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Teacher ID:</span>
                <span class="info-value">${displayId}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Position:</span>
                <span class="info-value">${teacherData.position || 'Not specified'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Department:</span>
                <span class="info-value">${teacherData.department || 'Not specified'}</span>
              </div>
            </div>
            
            <div class="payment-details">
              <div class="section-title">Payment Details</div>
              <div class="info-row">
                <span class="info-label">Payment Period:</span>
                <span class="info-value">${payment.paymentPeriod || format(new Date(payment.date), 'MMMM yyyy')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span class="info-value">${format(new Date(payment.date), 'PPP')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">${payment.method || 'Cash'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Base Amount:</span>
                <span class="info-value">${payment.baseAmount || payment.amount} AFN</span>
              </div>
              ${payment.allowances && payment.allowances.length > 0 ? `
                <div class="info-row">
                  <span class="info-label">Allowances:</span>
                  <span class="info-value">${payment.totalAllowances || 0} AFN</span>
                </div>
              ` : ''}
              ${payment.deductions && payment.deductions.length > 0 ? `
                <div class="info-row">
                  <span class="info-label">Deductions:</span>
                  <span class="info-value">${payment.totalDeductions || 0} AFN</span>
                </div>
              ` : ''}
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value">${payment.status}</span>
              </div>
            </div>
            
            <div class="amount-highlight">
              Total Amount Paid: ${payment.amount} AFN
            </div>
          </div>
          
          ${getFooterHtml('teacher')}
        </div>
      `;
      
      // Generate the complete document
      const printDocument = createPrintDocument(content, `Payment_Receipt_${teacherName}_${payment.id}`, styles);
      
      // Download as JPG directly - no window opening
      await downloadAsJPG(printDocument, `Payment_Receipt_${teacherName.replace(/\s+/g, '_')}_${format(new Date(payment.date), 'yyyy-MM-dd')}`, {
        onError: (error) => {
          console.error('JPG download error:', error);
          toast.error('Failed to download payment receipt');
        }
      });
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('Payment receipt downloaded as JPG');
      
    } catch (error) {
      console.error("Error downloading payment receipt:", error);
      toast.error("Failed to download payment receipt");
    }
  };
  
  if (payments.length === 0) {
    return (
      <Card className="p-4 text-center bg-gray-50 dark:bg-gray-800">
        <p className="text-gray-500">No payment history available</p>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Recent Payments</h3>
        {payments.length > 0 && (
          <Button variant="outline" size="sm" onClick={handlePrintPaymentHistory}>
            <Printer className="h-3.5 w-3.5 mr-1" />
            Print History
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Period</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {payments.slice(0, 5).map((payment) => (
              <tr key={payment.id} className="border-b dark:border-gray-700">
                <td className="px-4 py-2">{format(new Date(payment.date), 'PP')}</td>
                <td className="px-4 py-2">{payment.paymentPeriod || format(new Date(payment.date), 'MMMM yyyy')}</td>
                <td className="px-4 py-2 text-right">{payment.amount} AFN</td>
                <td className="px-4 py-2 text-center">
                  <Button variant="ghost" size="sm" onClick={() => handlePrintReceipt(payment)}>
                    <Printer className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
